import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormHelperText,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  QuestionSingleChoice, DigitalSubmissionSingleChoice, PartialBy, QuestionTypeV2,
} from '../../../redux/types';

export type Props = {
  question: QuestionSingleChoice;
  sectionId?: number;
  enrollmentId?: number;
  caseId?: number;
  disabled?: boolean;
  summarizedView?: boolean;
  onSave?: (val?: PartialBy<DigitalSubmissionSingleChoice, 'id'>) => Promise<void>
  defaultSubmission?: DigitalSubmissionSingleChoice;
  deprecated?: boolean;
}

const SingleChoiceQuestionInput: React.FC<Props> = ({
  question,
  sectionId,
  enrollmentId,
  caseId,
  disabled,
  defaultSubmission,
  summarizedView,
  onSave,
  deprecated,
}: Props) => {
  const [saveOp, setSaveOp] = useState<'idle'|'saving'|'saved'>('idle');
  const [error, setError] = useState<string>();

  useEffect(() => {
    setSaveOp('idle');
    setError(undefined);
  }, [caseId, enrollmentId, sectionId]);

  const getDigitalSubmissionObj = (selectedAnswer: number): PartialBy<DigitalSubmissionSingleChoice, 'id'> | undefined => {
    if (sectionId === undefined || enrollmentId === undefined) {
      return undefined;
    }
    if (defaultSubmission) {
      return {
        ...defaultSubmission,
        answer: selectedAnswer,
      };
    }
    return {
      answer: selectedAnswer,
      digitalQuestionId: question.id,
      digitalSectionId: sectionId,
      enrollmentId,
      digitalCaseId: caseId,
      type: QuestionTypeV2.SingleChoice,
    };
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (saveOp === 'saving') {
      return;
    }
    setSaveOp('idle');
    const idSelected = Number(event.target.value);
    const submissionObj = getDigitalSubmissionObj(idSelected);
    if (onSave && submissionObj) {
      setSaveOp('saving');
      onSave(submissionObj)
        .then(() => {
          setSaveOp('saved');
        })
        .catch((e) => {
          setError(e.message);
          setSaveOp('idle');
        });
    }
  };

  const qPayload = summarizedView
    ? question.payload?.filter((p) => defaultSubmission?.answer === p.id)
    : question.payload;

  if (deprecated) {
    return (
      <Box>
        <Typography sx={{ textDecorationLine: 'line-through' }} color="grey.400">
          {question.text}
        </Typography>
        <Typography variant="caption">Deprecated</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl error={!!error} component="fieldset">
        <Typography fontWeight={500} gutterBottom>{question.text}</Typography>
        <RadioGroup
          aria-labelledby="radio-question"
          name="radio-question-group"
          value={defaultSubmission?.answer || ''}
          onChange={handleChange}
        >
          {
            qPayload?.map((p) => (
              <FormControlLabel
                key={p.id}
                control={<Radio />}
                label={p.text}
                value={p.id}
                disabled={disabled}
              />
            ))
          }
        </RadioGroup>
        {
          !!error && (
            <FormHelperText>{error}</FormHelperText>
          )
        }
        {
          saveOp === 'saved' && (
            <FormHelperText data-testid="saved-status-caption">
              <Typography variant="caption" color="primary.main">
                <FormattedMessage id="submission.saved" />
              </Typography>
            </FormHelperText>
          )
        }
        {
          saveOp === 'saving' && (
            <FormHelperText data-testid="saving-status-caption">
              <FormattedMessage id="submission.saving" />
            </FormHelperText>
          )
        }
      </FormControl>
    </Box>
  );
};

export default SingleChoiceQuestionInput;
